import Vue from 'vue'

/**
 * 「在做」时间块的**定时提醒**（全局单例）。
 *
 * <p>用户诉求：给「在做」的块设一个提醒时间，到点后在电脑上提醒，
 * 由他决定「继续做（并可再定时）」还是「结束」。</p>
 *
 * <p>为什么做成全局模块而不是组件内部 `setTimeout`：</p>
 * <ul>
 *   <li>提醒时用户可能已经切到好友页 / 设置页，组件里的定时器会随组件销毁一起没了；</li>
 *   <li>定时要跨「添加记录」弹窗关闭、列表重渲染而存活；</li>
 *   <li>弹窗与定时器解耦：定时器只负责「到点 → 入队」，谁在渲染提醒弹窗由页面决定。</li>
 * </ul>
 *
 * <p>状态用 `Vue.observable`（与 `profileStorage.js` 的共享状态同一套路），
 * 组件里直接读 `timerState.timers` / `timerState.queue` 就有响应式。</p>
 *
 * <p>⚠️ key 用**时间块后端 id**，不是前端的 `evt.key`：</p>
 * <p>`evt.key` 是每次加载重新生成的临时标识（见 constants.nextBlockKey），刷新一次就变，
 * 拿它当 key 会在刷新后失联 —— 本地块还在、定时器却谁也不认识，也点不到「结束」。
 * 后端 id 稳定，且「结束」要打的就是 `PUT /blocks/{id}/status`。</p>
 *
 * <p>持久化到 localStorage：刷新页面后未到点的定时继续有效；
 * **已过期的直接丢弃，不补提醒**（几小时前的提醒弹出来只会打扰人）。</p>
 */

const STORAGE_KEY = 'taskblock-running-timers'
const STORAGE_VERSION = 2

/** @type {{timers: Object<string, Object>, queue: Array<Object>}} */
export const timerState = Vue.observable({
  /** 时间块 id（字符串）→ 定时条目 */
  timers: {},
  /** 已到点、等待用户处理的条目（可能同时到多个，按到达顺序排队） */
  queue: []
})

/** 时间块 id → setTimeout 句柄 */
const handles = {}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.list)) return []
    return parsed.list
  } catch (e) {
    return []
  }
}

function writeStore() {
  const list = Object.keys(timerState.timers).map(k => timerState.timers[k])
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, list }))
  } catch (e) {
    /* 隐私模式 / 超额：不影响内存里的定时 */
  }
}

/**
 * 页面启动时恢复未到点的定时。
 * ⚠️ 过期条目直接丢掉 —— 不补提醒。
 */
export function restoreTimers() {
  const now = Date.now()
  readStore().forEach(entry => {
    if (!entry || !entry.id) return
    if (!entry.at || entry.at <= now) return
    scheduleTimer(entry, { persist: false })
  })
  writeStore()
}

/**
 * 设定 / 覆盖一个定时。
 * @param {Object} entry 至少含 `id`（时间块后端 id，字符串）与 `at`（触发时刻，ms）；
 *                另外可带 `label`（提醒时刻文案）/ `title`（事项）/ `catLabel` / `catColor` /
 *                `startedAt`（块的实际开始时刻，ms，用于弹窗里显示"已经开始多久"）
 */
export function scheduleTimer(entry, opts) {
  if (!entry || !entry.id || !entry.at) return
  const id = String(entry.id)
  // minute（当天分钟坐标）缺了就按 at 推：编辑已有的「在做」块时要靠它把定时回填到开关上。
  // 「继续（再定时）」走的是 `{...entry, at, label}` 展开，老 minute 会过期，所以这里统一兜底。
  if (entry.minute == null) {
    const d = new Date(entry.at)
    entry.minute = d.getHours() * 60 + d.getMinutes()
  }
  clearTimeout(handles[id])
  Vue.set(timerState.timers, id, entry)
  const delay = Math.max(0, entry.at - Date.now())
  handles[id] = setTimeout(() => fire(id), delay)
  if (!opts || opts.persist !== false) writeStore()
}

/** 取消（块被结束 / 删除 / 状态流转后） */
export function cancelTimer(id) {
  if (!id) return
  const key = String(id)
  clearTimeout(handles[key])
  delete handles[key]
  if (timerState.timers[key]) {
    Vue.delete(timerState.timers, key)
    writeStore()
  }
}

/** 该块当前是否有在跑的定时 */
export function timerOf(id) {
  if (!id) return null
  return timerState.timers[String(id)] || null
}

function fire(id) {
  const entry = timerState.timers[id]
  if (!entry) return
  clearTimeout(handles[id])
  delete handles[id]
  Vue.delete(timerState.timers, id)
  writeStore()
  notifyDesktop(entry)
  timerState.queue.push(entry)
}

function notifyDesktop(entry) {
  try {
    if (typeof window === 'undefined' || !window.Notification) return
    if (window.Notification.permission !== 'granted') return
    const n = new window.Notification('时间到 · ' + (entry.catLabel || '在做'), {
      body: `${entry.title || '（无事项）'}\n回到页面选择「继续」或「结束」`,
      tag: 'taskblock-running-' + String(entry.id),
      // 不自动消失：用户可能没盯着屏幕
      requireInteraction: true
    })
    n.onclick = () => {
      try {
        window.focus()
        n.close()
      } catch (e) { /* ignore */ }
    }
  } catch (e) {
    /* 通知被环境禁用时不影响页面内弹窗 */
  }
}

/**
 * 申请桌面通知权限。必须在**用户手势**里调（开启定时开关时），
 * 否则浏览器（尤其 Safari / Firefox）会直接拒绝。
 */
export function ensureNotifyPermission() {
  try {
    if (typeof window === 'undefined' || !window.Notification) return
    if (window.Notification.permission === 'default') {
      window.Notification.requestPermission()
    }
  } catch (e) { /* ignore */ }
}

/** 桌面通知是否已授权（用于弹窗里提示"提醒可能收不到"） */
export function notifyGranted() {
  try {
    return !!(typeof window !== 'undefined' && window.Notification && window.Notification.permission === 'granted')
  } catch (e) {
    return false
  }
}

/**
 * 用户在提醒弹窗里做了选择。
 * @param {'continue'|'end'|'dismiss'} action dismiss = 关掉弹窗，块继续「在做」，不再定时
 * @param {number} [nextAt] action==='continue' 时的下一次触发时刻（ms）
 * @param {string} [nextLabel] action==='continue' 时的新提醒时间文案
 */
export function resolvePending(action, nextAt, nextLabel) {
  const entry = timerState.queue.shift()
  if (!entry) return
  if (action === 'continue' && nextAt) {
    scheduleTimer({ ...entry, at: nextAt, label: nextLabel || entry.label })
  }
}
